/**
 * `#uc2Contrasts` (design `2026-10-02-uc2-tabs-analytics-design.md` §Tabs, re-presented
 * after the client round of 2 October 2026): the pairs of alike watersheds that sit in
 * **different** classes — one with positive change, one without — ranked by how alike
 * they are, one `Uc2PairCard` each.
 *
 * This is the Similar tab's answer with nothing clicked: precomputed on `uc2/summary` from
 * the same stored top-k lists the per-watershed cards read, so changing the practice group
 * costs no request. At country level the list is filtered to the pairs that touch that
 * country.
 *
 * The **adoption figures the two bars draw are not in the pair** — the engine stores the
 * gap and the learning flag, not the two sides' own numbers. They come from the units rows
 * the pane is already holding (`adoptionOf`), which is the same payload the practices chip
 * paints the map from, so a bar here and a colour there can never disagree. A side the
 * rows do not carry draws no bar, and the sentence still has the gap to speak from.
 */
import { useTranslation } from 'react-i18next';

import type { IntelUc2ContrastPair } from '../../lib/api';
import { Uc2Notes } from './Uc2Notes';
import { Uc2PairCard } from './Uc2PairCard';
import { pairLookOut, pairNoteBranch } from './uc2Captions';
import { UC2_PROGRAMME_SCOPE, unitInScope } from './uc2Constants';

export interface Uc2ContrastsProps {
  pairs?: IntelUc2ContrastPair[] | undefined;
  /** `'programme'`, or the admin0 code the country level is reading. */
  scope: string;
  /** `#uc2Practice` — which group's bars, gap and learning flag the cards show. */
  practice: string;
  /** That group's label, and the same word lower-cased for mid-sentence use. */
  practiceLabel: string;
  practiceLower: string;
  /** One watershed's adoption of the selected group, from the units rows. */
  adoptionOf: (unitId: string) => number | null;
  /** `summary.programme_means[group]` — the tick every bar is read against. */
  programmeMean?: number | null;
  /** How many cards at most; the design stores up to ten. */
  limit?: number;
}

/** A pair belongs to a country scope when it says so, or when either id starts with it. */
function pairInScope(pair: IntelUc2ContrastPair, scope: string): boolean {
  if (scope === UC2_PROGRAMME_SCOPE) return true;
  if (pair.countries?.length) return pair.countries.includes(scope);
  return unitInScope(pair.a.unit_id, scope) || unitInScope(pair.b.unit_id, scope);
}

export function Uc2Contrasts({
  pairs,
  scope,
  practice,
  practiceLabel,
  practiceLower,
  adoptionOf,
  programmeMean,
  limit = 10,
}: Uc2ContrastsProps) {
  const { t } = useTranslation();
  const rows = (pairs ?? []).filter((p) => pairInScope(p, scope)).slice(0, limit);

  return (
    <div id="uc2Contrasts" className="result-box">
      <h5>{t('intel.uc2.contrastsTitle')}</h5>
      <div className="result-pad">
        {rows.length ? (
          <ul className="uc2-similar">
            {rows.map((pair) => {
              const gap = typeof pair.gaps?.[practice] === 'number' ? pair.gaps[practice]! : null;
              const pct = Number.isFinite(pair.similarity)
                ? Math.round(Math.min(1, Math.max(0, pair.similarity)) * 100)
                : 0;
              const id = `${pair.a.unit_id}-${pair.b.unit_id}`;
              const learning = Boolean(pair.learning?.[practice]);
              const missingSide = adoptionOf(pair.a.unit_id) == null ? 'a' : 'b';
              const lookOut = pairLookOut(
                pairNoteBranch(gap, learning),
                { a: pair.a.name, b: pair.b.name },
                practiceLower,
                gap,
                missingSide,
              );
              return (
                <Uc2PairCard
                  key={id}
                  testId={`uc2-contrast-${id}`}
                  a={{
                    id: pair.a.unit_id,
                    name: pair.a.name,
                    category: pair.a.category,
                    value: adoptionOf(pair.a.unit_id),
                  }}
                  b={{
                    id: pair.b.unit_id,
                    name: pair.b.name,
                    category: pair.b.category,
                    value: adoptionOf(pair.b.unit_id),
                  }}
                  similarityPct={pct}
                  practiceLabel={practiceLabel}
                  practiceLower={practiceLower}
                  gap={gap}
                  learning={learning}
                  programmeMean={programmeMean ?? null}
                  note={
                    <Uc2Notes
                      testId={`uc2-contrast-note-${id}`}
                      rows={[
                        {
                          label: t('intel.uc2.note.happening'),
                          lines: [
                            t('intel.uc2.note.pairHappening', {
                              a: pair.a.name,
                              b: pair.b.name,
                              pct,
                            }),
                          ],
                        },
                        {
                          label: t('intel.uc2.note.lookOut'),
                          lines: [t(lookOut.key, lookOut.options ?? {})],
                        },
                      ]}
                    />
                  }
                />
              );
            })}
          </ul>
        ) : (
          <p className="note" data-testid="uc2-contrasts-empty">
            {t('intel.uc2.blockPending')}
          </p>
        )}
      </div>
    </div>
  );
}

export default Uc2Contrasts;
